import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addPrivateProsecutionReport,
  editPrivateProsecution,
} from "services/privateProsecutionService";
import { PrivateProsecution } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

interface Props {
  privateProsecution: PrivateProsecution;
  setRefetch: () => void;
  onCancel: () => void;
}

const AddPrivateProsecutionReport: React.FC<Props> = ({
  privateProsecution,
  onCancel,
  setRefetch,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [formState, setFormState] = useState({
    type: "",
    private_prosecution_report_summary: "",
    report_file: null,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.type) return "Type is Required.";
    if (!formState.private_prosecution_report_summary)
      return "Summary is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    // Convert date string to Date object if needed
    const payload = {
      ...formState,
      status: "pending",
      created_by: loginData?.full_name,
      private_prosecution: privateProsecution?.id,
    };

    addPrivateProsecutionReport(payload)
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        setRefetch();
        onCancel();
      })
      .catch((err) => {
        editPrivateProsecution({
          id: privateProsecution?.id,
          status: formState?.type,
        })
          .then(() => {
            showTooltip(t("failed_to_submit"), "error");
          })
          .catch((error) => {
            showTooltip(errorHandlerOnForm(error), "error");
          });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onCancel();
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[550px] max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-800">
          Private Prosecution Report
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col mt-4 ">
            <label className="text-sm text-[#747171] font-[inter] font-[400] flex-col-2 mb-2">
              Select Type *
            </label>
            <select
              className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
              name="type"
              value={formState?.type ?? ""}
              onChange={handleChange}
            >
              <option value={""}>Select Type</option>
              <option value={"on_litigation"}>On Litigation</option>
              <option value={"on_appeal"}>On Appeal</option>
              <option value={"final_verdict_given"}>Final Verdict Given</option>
              <option value={"withdrawn"}>Withdrawn</option>
            </select>
          </div>
          <div className="flex flex-col col-span-2">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Summary *
            </label>
            <textarea
              rows={5}
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="private_prosecution_report_summary"
              value={formState.private_prosecution_report_summary}
              onChange={handleChange}
            />
          </div>

          <div className="mt-4">
            <FileUpload
              label="Private Prsecution Decision"
              fileTypes="image/*, .pdf"
              info="Upload PDF or Image Files "
              // required={true}
              onChange={(attachment) =>
                setFormState((prev) => ({
                  ...prev,
                  report_file: attachment as any,
                }))
              }
            />
          </div>

          <div className="mt-6 flex justify-between gap-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
              onClick={onCancel}
              type="button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                        font-[Poppins] cursor-pointer font-[500] w-full
                        text-base rounded-md hover:bg-white hover:text-[#E90101]
                        ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPrivateProsecutionReport;
